import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shopify } from "@/lib/api";
import { t } from "@/lib/i18n";
import { optimizeImg } from "@/lib/img";

/**
 * Premium colorway strip: shows a small vertical thumbnail per sibling product
 * that shares the same "family" (title after ' - '). Marcini has separate
 * Shopify products per color, but customers see them grouped as variants.
 */
export default function ColorwaySwitcher({ product }) {
  const { data: siblings = [] } = useQuery({
    queryKey: ["colorways", product.handle],
    queryFn: () => shopify.colorways(product.handle),
    staleTime: 60_000,
    enabled: !!product?.handle,
  });

  if (siblings.length < 2) return null;
  const active = siblings.find((s) => s.handle === product.handle);

  return (
    <div className="mt-8" data-testid="colorway-switcher">
      <div className="flex items-center justify-between mb-3">
        <p className="text-eyebrow">
          {t.pdp.color}
          <span className="mx-2 text-[color:var(--marcini-fg-3)]">·</span>
          <span className="text-[color:var(--marcini-fg-2)] normal-case tracking-normal font-normal">
            {active?.color_name || product.color_name}
          </span>
        </p>
        <span className="text-eyebrow text-[color:var(--marcini-fg-3)]">
          {siblings.length} {t.pdp.colorCount}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
        {siblings.map((s) => {
          const isActive = s.handle === product.handle;
          return (
            <Link
              key={s.handle}
              to={`/products/${s.handle}`}
              data-testid={`colorway-${s.handle}`}
              className="flex-shrink-0 group focus:outline-none"
              title={s.color_name}
            >
              <div
                className={`relative w-16 h-20 md:w-[68px] md:h-[86px] overflow-hidden border transition-colors duration-300 ${
                  isActive
                    ? "border-black"
                    : "border-[color:var(--marcini-border)] group-hover:border-black"
                }`}
              >
                <img
                  src={optimizeImg(s.thumbnail, 200)}
                  alt={s.color_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {!s.available && (
                  <div className="absolute inset-0 bg-white/55 flex items-center justify-center">
                    <span className="text-[9px] tracking-widest">{t.common.soldOut.toUpperCase()}</span>
                  </div>
                )}
                {isActive && (
                  <div className="absolute inset-x-0 bottom-0 h-[3px] bg-black" />
                )}
              </div>
              <p
                className={`mt-2 text-[10px] tracking-[0.18em] uppercase text-center whitespace-nowrap ${
                  isActive ? "text-black" : "text-[color:var(--marcini-fg-2)]"
                }`}
              >
                {s.color_name.split(" ")[0]}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
